import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  Chip,
  IconButton,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  AVAILABLE_TV_CHARTS,
  TvTemplate,
  getTvTemplateState,
  saveTvTemplateState,
} from "./TvDashboardTemplatesConfig";

const createId = (name: string) =>
  name.toLowerCase().trim().replace(/\s+/g, "-") + "-" + Date.now();

export default function TvDashboardTemplatesAdmin() {
  const [state, setState] = useState(() => getTvTemplateState());
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    state.templates[0]?.id ?? null,
  );

  const selectedTemplate =
    state.templates.find((template) => template.id === selectedTemplateId) ??
    null;

  const updateState = (nextState: typeof state) => {
    setState(nextState);
    saveTvTemplateState(nextState);
  };

  const createTemplate = () => {
    const newTemplate: TvTemplate = {
      id: createId("Dashboard Template"),
      name: "Dashboard Template",
      description: "",
      chartIds: [],
      overviewDurationMs: 12000,
      detailDurationMs: 7000,
    };

    const nextState = {
      ...state,
      templates: [...state.templates, newTemplate],
    };

    updateState(nextState);
    setSelectedTemplateId(newTemplate.id);
    toast.success("Template created");
  };

  const updateTemplate = (template: TvTemplate) => {
    const nextState = {
      ...state,
      templates: state.templates.map((item) =>
        item.id === template.id ? template : item,
      ),
    };

    updateState(nextState);
  };

  const deleteTemplate = (templateId: string) => {
    const nextTemplates = state.templates.filter(
      (template) => template.id !== templateId,
    );

    const nextState = {
      templates: nextTemplates,
      liveTemplateId:
        state.liveTemplateId === templateId ? null : state.liveTemplateId,
    };

    updateState(nextState);
    setSelectedTemplateId(nextTemplates[0]?.id ?? null);
    toast.success("Template deleted");
  };

  const setLiveTemplate = (templateId: string) => {
    const nextState = {
      ...state,
      liveTemplateId: templateId,
    };

    updateState(nextState);
    toast.success("Live TV template updated");
  };

  const toggleChart = (chartId: TvTemplate["chartIds"][number]) => {
    if (!selectedTemplate) return;

    const exists = selectedTemplate.chartIds.includes(chartId);

    const nextTemplate = {
      ...selectedTemplate,
      chartIds: exists
        ? selectedTemplate.chartIds.filter((id) => id !== chartId)
        : [...selectedTemplate.chartIds, chartId],
    };

    updateTemplate(nextTemplate);
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={800} gutterBottom>
        TV Dashboard Templates
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create templates. Choose which
        charts each template displays, then set one template as live.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Templates</Typography>

              <Button
                size="small"
                variant="contained"
                startIcon={<Add />}
                onClick={createTemplate}
              >
                New
              </Button>
            </Stack>

            <Stack spacing={1.5}>
              {state.templates.map((template) => {
                const isSelected = template.id === selectedTemplateId;
                const isLive = template.id === state.liveTemplateId;

                return (
                  <Paper
                    key={template.id}
                    variant="outlined"
                    onClick={() => setSelectedTemplateId(template.id)}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderColor: isSelected ? "primary.main" : "divider",
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1}
                      >
                        <Typography fontWeight={700}>
                          {template.name}
                        </Typography>

                        {isLive && (
                          <Chip
                            label="LIVE"
                            color="success"
                            size="small"
                            sx={{ fontWeight: 800 }}
                          />
                        )}
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        {template.chartIds.length} chart(s) selected
                      </Typography>

                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant={isLive ? "outlined" : "contained"}
                          disabled={isLive}
                          onClick={(event) => {
                            event.stopPropagation();
                            setLiveTemplate(template.id);
                          }}
                        >
                          {isLive ? "Live" : "Set Live"}
                        </Button>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteTemplate(template.id);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 2 }}>
            {!selectedTemplate ? (
              <Typography color="text.secondary">
                Select or create a template.
              </Typography>
            ) : (
              <Stack spacing={3}>
                <Typography variant="h6">Edit Template</Typography>

                <TextField
                  label="Template Name"
                  value={selectedTemplate.name}
                  onChange={(event) =>
                    updateTemplate({
                      ...selectedTemplate,
                      name: event.target.value,
                    })
                  }
                  fullWidth
                />

                <TextField
                  label="Description"
                  value={selectedTemplate.description ?? ""}
                  onChange={(event) =>
                    updateTemplate({
                      ...selectedTemplate,
                      description: event.target.value,
                    })
                  }
                  fullWidth
                  multiline
                  minRows={2}
                />

                <Divider />

                <Box>
                  <Typography fontWeight={700} mb={1}>
                    Choose charts to display
                  </Typography>

                  <Grid container spacing={2}>
                    {AVAILABLE_TV_CHARTS.map((chart) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={chart.id}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedTemplate.chartIds.includes(
                                  chart.id,
                                )}
                                onChange={() => toggleChart(chart.id)}
                              />
                            }
                            label={
                              <Box>
                                <Typography fontWeight={700}>
                                  {chart.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {chart.description}
                                </Typography>
                              </Box>
                            }
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Divider />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Overview Duration MS"
                    type="number"
                    value={selectedTemplate.overviewDurationMs}
                    onChange={(event) =>
                      updateTemplate({
                        ...selectedTemplate,
                        overviewDurationMs: Number(event.target.value),
                      })
                    }
                    fullWidth
                  />

                  <TextField
                    label="Detail Duration MS"
                    type="number"
                    value={selectedTemplate.detailDurationMs}
                    onChange={(event) =>
                      updateTemplate({
                        ...selectedTemplate,
                        detailDurationMs: Number(event.target.value),
                      })
                    }
                    fullWidth
                  />
                </Stack>

                <Button
                  variant="contained"
                  onClick={() => {
                    saveTvTemplateState(state);
                    toast.success("Template saved");
                  }}
                >
                  Save Template
                </Button>
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}